import { GoATable, GoACheckbox, GoAFormItem, GoAInput, GoAButton } from '@abgov/react-components';
import { FC, useState } from 'react';
import Select, { InputActionMeta } from 'react-select';
import { AuthenticationService } from '../../../services/authentication/AuthenticationService';
import { FindDefaultRate } from '../../../services/ContractRateService';
import { convertCostingActivitiesToArrayOptions } from '../../../services/domainServices/CostingActivityService';
import { convertCrewTypesToArrayOptions } from '../../../services/domainServices/CrewTypeService';
import { convertFireNumbersToArrayOptions } from '../../../services/domainServices/FireNumberService';
import { convertRateTypesToArrayOptions } from '../../../services/domainServices/RateTypeService';
import { convertRateUnitsToArrayOptions, RateUnitService } from '../../../services/domainServices/RateUnitService';
import { validateDecimal } from '../../../utils/checkInput';
import { FlyingHours, IFlyingHours } from '../../../model/IFlyingHours';
import { useAuth } from 'react-oidc-context';

import { convertAccountsToArrayOptions } from '../../../services/1GXCoding/AccountService';
import { convertCostCentersToArrayOptions } from '../../../services/1GXCoding/CostCenterService';
import { convertFundsToArrayOptions } from '../../../services/1GXCoding/FundService';
import { convertInternalOrdersToArrayOptions } from '../../../services/1GXCoding/InternalOrderService';
import { IArrayOption } from '../../../model/IArrayOption';
import { IFireNumber } from '../../../model/domainServices/IFireNumber';
import { ICrewType } from '../../../model/domainServices/ICrewType';
import { IContractRates } from '../../../model/HiringContract/IContractRates';
import moment from 'moment';
import { IDomainData } from '../helpers/DomainDataHelper';
import { calculateCost } from '../../../utils/commonMethods';
import ActionEvents from '../../../model/enum/ActionEvents';
import { FlyingDetailCRUD } from '../../../operations/FlyingDetailCRUD';
import { IFlightReport } from '../../../model/IFlightReport';
import { EmptyGuid } from '../../../utils/Constants';

interface IFlyingHoursEditTableProps {
  flightReport: IFlightReport;
  noFlyDay: boolean;
  flyingHours: IFlyingHours[]; //immutable
  onFlyingHoursChange: (hours: IFlyingHours[]) => void;
  onValidate: (elementName: string, eventName: string, value: any) => void;
  contractRates: IContractRates[];
  domainData: IDomainData;
  fireNumberOptions: IArrayOption[];
  crewTypeOptions: IArrayOption[];
  costingActivityOptions: IArrayOption[];
  rateTypeOptions: IArrayOption[];
  rateUnitOptions: IArrayOption[];
  internalOrderOptions: IArrayOption[];
  costCenterOptions: IArrayOption[];
  fundOptions: IArrayOption[];
  accountOptions: IArrayOption[];
}
const FlyingHoursEditTable: FC<IFlyingHoursEditTableProps> = ({
  flightReport,
  noFlyDay,
  flyingHours,
  onFlyingHoursChange,
  onValidate,
  contractRates,
  domainData,
  fireNumberOptions,
  crewTypeOptions,
  costingActivityOptions,
  rateTypeOptions,
  rateUnitOptions,
  internalOrderOptions,
  costCenterOptions,
  fundOptions,
  accountOptions,
}) => {
  const auth = useAuth();
  const [allSelected, setAllSelected] = useState(false);

  const selectPlaceHolder = '--Select--';
  const checkAll = (name: string, checked: boolean, value: string) => {
    const updated = flyingHours.map((x) => ({
      ...x,
      isChecked: checked,
    }));

    onFlyingHoursChange(updated);
    setAllSelected(checked);
  };

  function getDefaultRateUnit(record: IFlyingHours) {
    if (!record || !record.rateUnitList) {
      return 'selectPlaceHolder';
    }
    const rateUnit = record.rateUnitList.find((t: { value: any }) => t.value === record?.rateUnitId);
    return rateUnit ? rateUnit : selectPlaceHolder;
  }

  async function addNew() {
    const flyingHour = new FlyingHours();
    flyingHour.flightReportId = flightReport?.flightReportId;
    flyingHour.createTimestamp = moment(new Date()).format('yyyy-MM-DD');
    flyingHour.createUserId = auth?.user?.profile?.name ?? 'wildfire';
    flyingHour.updateTimestamp = null;
    flyingHour.expenseType = 'FlyingHours';

    //Dropdown
    flyingHour.fireNumberList = fireNumberOptions;
    flyingHour.crewTypeList = crewTypeOptions;
    flyingHour.costingActivityList = costingActivityOptions;
    flyingHour.rateTypeList = rateTypeOptions;
    flyingHour.rateUnitList = rateUnitOptions;

    //1GX coding - financial
    flyingHour.internalOrderList = internalOrderOptions;
    flyingHour.costCenterList = costCenterOptions;
    flyingHour.fundList = fundOptions;
    flyingHour.accountList = accountOptions;

    //Default Fund
    if (fundOptions) {
      if (fundOptions.find((x) => x.value === '41')) flyingHour.fund = '41';
    }

    //Default Account
    if (accountOptions) {
      if (accountOptions.find((x) => x.value === '6020700210')) flyingHour.account = '6020700210';
    }

    const rateType = FindDefaultRate(contractRates, flightReport.contractType as string);
    if (rateType > 0) {
      const rt = domainData.rateTypes.find((t) => t.oracleId === rateType);
      const rates = contractRates.find((value) => value.crtyId === rateType);

      flyingHour.rateTypeId = rt ? rt.rateTypeId : '';
      flyingHour.rateTypeEntity = rt;
      flyingHour.ratePerUnit = rates ? rates?.ratePerUnit : 0;

      if (rates) {
        var rateUnit: any = await RateUnitService.getByOracleId(rates?.cruId ?? 0);

        flyingHour.rateUnitId = rateUnit ? rateUnit?.data?.rateUnitId : undefined;
        flyingHour.rateUnitEntity = rateUnit?.data;
      }
    }

    const newValue = [...flyingHours, flyingHour];
    onFlyingHoursChange(newValue);
  }
  const checkRow = (index: number, checked: boolean) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;

      return {
        ...h,
        isChecked: checked,
      };
    });

    onFlyingHoursChange(updated);
  };

  const setFireNumber = async (index: number, value: IArrayOption) => {
    const updated = flyingHours.map(async (h, idx) => {
      if (idx !== index) return h;

      if (value.value) {
        const fireNumber = domainData.fireNumbers.find((t: IFireNumber) => t.id === value.value);
        // update the internal order
        // Extracting the fire identifier (e.g., SWF001)
        // Extracting the year (e.g., 2024)
        // Applying the internal order format (FF + fire identifier + last two digits of year = FFSWF00124)
        //     If a user manually changes the internal order after auto-defaulting
        const internalOrder = `FF${value.fireNumber}${value.fireYear?.toString().slice(-2)}`;
        let optionsData = await FlyingDetailCRUD.getInternalOrder(internalOrder, auth.user?.access_token);
        let internalOrderList: IArrayOption[] = [];
        let internalOrdersFound = false;
        if (!optionsData || !optionsData.data || !optionsData.data.data || optionsData.data.data.length > 0) {
          internalOrderList = convertInternalOrdersToArrayOptions(optionsData.data.data);
          internalOrdersFound = true;
        } else {
          internalOrderList = internalOrderOptions;
        }
        const newCostingActivityOptions = h.costingActivityList?.filter((ca: IArrayOption) => ca.label.startsWith('FS'));
        return {
          ...h,
          fireNumberId: value.value,
          fireNumber: value.fireNumber,
          fireYear: value.fireYear,
          fireNumberEntity: fireNumber,
          costingActivityId: EmptyGuid,
          costingActivityCode: '',
          costingActivityEntity: undefined,
          internalOrder: internalOrdersFound ? internalOrder : null,
          internalOrderList: internalOrderList,
          costingActivityList: newCostingActivityOptions,
          internalOrdersEnabled: !internalOrdersFound,
        };
      } else {
        return {
          ...h,
          fireNumberId: undefined,
          fireNumber: '',
          fireYear: undefined,
          fireNumberEntity: undefined,
          costingActivityId: EmptyGuid,
          costingActivityCode: '',
          costingActivityEntity: undefined,
          internalOrdersEnabled: true,
          internalOrder: null,
          internalOrderList: internalOrderOptions,
          costingActivityList: costingActivityOptions,
        };
      }
    });

    onFlyingHoursChange(await Promise.all(updated));
  };

  const setCrewType = (index: number, value: IArrayOption) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;

      if (value.value) {
        var crewType = domainData.crewTypes.find((t: ICrewType) => t.crewTypeId === value.value);
        return {
          ...h,
          crewTypeId: value.value,
          crewTypeEntity: crewType,
        };
      } else {
        return {
          ...h,
          crewTypeId: null,
          crewTypeEntity: undefined,
        };
      }
    });

    onFlyingHoursChange(updated);
  };

  const setCostingActivity = (index: number, value: IArrayOption) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;

      const costingActivity = domainData.costingActivities.find((t) => t.costingActivityId === value.value);
      return {
        ...h,
        costingActivityId: value.value,
        costingActivityCode: value.code,
        costingActivityEntity: costingActivity,
      };
    });

    onFlyingHoursChange(updated);
  };
  const setRateType = async (index: number, value: IArrayOption) => {
    const updated = await Promise.all(
      flyingHours.map(async (h, idx) => {
        if (idx !== index) return h;

        const rateType = domainData.rateTypes.find((t) => t.rateTypeId === value.value);

        let ratePerUnit;
        if (rateType?.oracleId && contractRates) {
          ratePerUnit = contractRates.find((value) => value.crtyId === rateType.oracleId);
        }

        let rateUnit;

        if (ratePerUnit && ratePerUnit.cruId) {
          rateUnit = await RateUnitService.getByOracleId(ratePerUnit.cruId);
        }

        return {
          ...h,
          rateUnitId: rateUnit?.data?.rateUnitId ?? '',
          rateUnitEntity: rateUnit?.data,
          rateTypeId: value.value,
          rateTypeEntity: rateType,
          ratePerUnit: ratePerUnit?.ratePerUnit,
        };
      }),
    );

    onFlyingHoursChange(updated);
  };
  const setNoOfUnits = (index: number, value: string) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;

      if (validateDecimal(value, 1)) {
        const num = Number(value);

        return {
          ...h,
          noOfUnits: num,
          isNoOfUnitsError: false,
          noOfUnitsError: '',
          cost: calculateCost(value, h.ratePerUnit),
        };
      } else {
        return {
          ...h,
          noOfUnits: value,
          isNoOfUnitsError: true,
          noOfUnitsError: '1 decimal valid',
        };
      }
    });

    onFlyingHoursChange(updated);
  };

  const setRateUnit = (index: number, value: IArrayOption) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;

      const rateUnit = domainData.rateUnits.find((t) => t.rateUnitId === value.value);
      return {
        ...h,
        rateUnitId: value.value,
        rateUnitEntity: rateUnit,
      };
    });

    onFlyingHoursChange(updated);
  };

  const setRatePerUnit = (index: number, value: string) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;
      const num = parseFloat(value);
      if (validateDecimal(value, 1)) {
        return {
          ...h,
          ratePerUnit: num,
          isRatePerUnitError: false,
          ratePerUnitError: '',
          cost: calculateCost(h.noOfUnits, num),
        };
      } else {
        return {
          ...h,
          ratePerUnit: num,
          isRatePerUnitError: true,
          ratePerUnitError: '2 decimal valid',
        };
      }
    });

    onFlyingHoursChange(updated);
  };
  const setInternalOrder = (index: number, value: IArrayOption) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;
      if (value.value) {
        return {
          ...h,
          internalOrder: value.value,
        };
      } else
        return {
          ...h,
          internalOrder: undefined,
        };
    });

    onFlyingHoursChange(updated);
  };

  const setCostCenter = (index: number, value: IArrayOption) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;
      if (value.value) {
        return {
          ...h,
          costCenter: value.value,
        };
      } else
        return {
          ...h,
          costCenter: undefined,
        };
    });

    onFlyingHoursChange(updated);
  };

  const setFund = (index: number, value: IArrayOption) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;
      if (value.value) {
        return {
          ...h,
          fund: value.value,
        };
      } else
        return {
          ...h,
          fund: undefined,
        };
    });

    onFlyingHoursChange(updated);
  };

  const setAccount = (index: number, value: IArrayOption) => {
    const updated = flyingHours.map((h, idx) => {
      if (idx !== index) return h;
      if (value.value) {
        return {
          ...h,
          account: value.value,
        };
      } else
        return {
          ...h,
          account: undefined,
        };
    });

    onFlyingHoursChange(updated);
  };
  const onFireNumberChange = async (index: number, searchValue: string = '') => {
    try {
      const fireNumberResponse = await FlyingDetailCRUD.getFireNumbers(flightReport.flightReportDate, searchValue);

      if (fireNumberResponse.status) {
        const updated = flyingHours.map((h, idx) => {
          if (idx !== index) return h;
          return {
            ...h,
            fireNumberList: convertFireNumbersToArrayOptions(fireNumberResponse.data.data),
          };
        });

        onFlyingHoursChange(updated);
      }
    } catch (Error) {
      console.log('Error', Error);
    }
  };

  //Crew type
  const onCrewTypeChange = async (index: number, searchValue: string = '') => {
    try {
      const crewTypeResponse = await FlyingDetailCRUD.getCrewTypes(flightReport.flightReportDate, searchValue);

      const updated = flyingHours.map((h, idx) => {
        if (idx !== index) return h;
        return {
          ...h,
          crewTypeList: convertCrewTypesToArrayOptions(crewTypeResponse.data.data),
        };
      });

      onFlyingHoursChange(updated);
    } catch (Error) {
      console.log('Error', Error);
    }
  };

  //Costing activity
  const onCostingActivityChange = async (index: number, searchValue: string = '') => {
    try {
      const costingActivityResponse = await FlyingDetailCRUD.getCostingActivities(searchValue);

      if (costingActivityResponse.status) {
        const updated = flyingHours.map((h, idx) => {
          if (idx !== index) return h;
          return {
            ...h,
            costingActivityList: convertCostingActivitiesToArrayOptions(costingActivityResponse.data.data),
          };
        });

        onFlyingHoursChange(updated);
      }
    } catch (Error) {
      console.log('Error', Error);
    }
  };

  //Rate type
  const onRateTypeChange = async (index: number, searchValue: string = '') => {
    try {
      const rateTypeResponse = await FlyingDetailCRUD.getRateTypes(flightReport.flightReportDate, searchValue);
      const updated = flyingHours.map((h, idx) => {
        if (idx !== index) return h;
        return {
          ...h,
          rateTypeList: convertRateTypesToArrayOptions(rateTypeResponse.data.data),
        };
      });

      onFlyingHoursChange(updated);
    } catch (Error) {
      console.log('Error', Error);
    }
  };

  //Rate unit
  const onRateUnitChange = async (index: number, searchValue: string = '') => {
    try {
      const rateUnitResponse: any = await FlyingDetailCRUD.getRateUnits(flightReport.flightReportDate, searchValue);
      const updated = flyingHours.map((h, idx) => {
        if (idx !== index) return h;
        return {
          ...h,
          rateUnitList: convertRateUnitsToArrayOptions(rateUnitResponse.data.data),
        };
      });

      onFlyingHoursChange(updated);
    } catch (Error) {
      console.log('Error', Error);
    }
  };
  const onCostCenterChange = async (index: number, searchValue: string = '') => {
    try {
      const costCenterResponse = await FlyingDetailCRUD.getCostCenter(searchValue, auth.user?.access_token as string);
      const updated = flyingHours.map((h, idx) => {
        if (idx !== index) return h;
        return {
          ...h,
          costCenterList: convertCostCentersToArrayOptions(costCenterResponse.data.data),
        };
      });

      onFlyingHoursChange(updated);
    } catch (Error) {
      console.log('Error', Error);
    }
  };

  //Fund
  const onFundChange = async (index: number, searchValue: string = '') => {
    try {
      const fundResponse: any = await FlyingDetailCRUD.getFund(searchValue, auth.user?.access_token as string);
      const updated = flyingHours
        .filter((x) => !x.deleted)
        .map((h, idx) => {
          if (idx !== index) return h;
          return {
            ...h,
            fundList: convertFundsToArrayOptions(fundResponse.data.data),
          };
        });

      onFlyingHoursChange(updated);
    } catch (Error) {
      console.log('Error', Error);
    }
  };

  const onAccountChange = async (index: number, searchValue: string = '') => {
    try {
      const options = await FlyingDetailCRUD.getAccount(searchValue, auth.user?.access_token as string);
      const updated = flyingHours
        .filter((x) => !x.deleted)
        .map((h, idx) => {
          if (idx !== index) return h;
          return {
            ...h,
            accountList: convertAccountsToArrayOptions(options.data.data),
          };
        });

      onFlyingHoursChange(updated);
    } catch (Error) {
      console.log('Error', Error);
    }
  };

  const onInternalOrdersChange = async (index: number, searchValue: string) => {
    const options = await FlyingDetailCRUD.getInternalOrder(searchValue, auth.user?.access_token);

    const updated = flyingHours
      .filter((x) => !x.deleted)
      .map((h, idx) => {
        if (idx !== index) return h;
        return {
          ...h,
          internalOrderList: convertInternalOrdersToArrayOptions(options.data.data),
        };
      });

    onFlyingHoursChange(updated);
  };

  return (
    <>
      <div className='white-background horizontal-scrollbar'>
        <GoATable width='100%'>
          <thead>
            <tr>
              {flyingHours.length > 0 ? (
                <th className='width50px goa-checkbox-table-td'>
                  <GoACheckbox name='chkAll' text='' value='0' checked={allSelected} onChange={checkAll}></GoACheckbox>
                </th>
              ) : (
                <></>
              )}
              <th className='width185px'>Fire number</th>
              <th className='width215px'>Crew type on board</th>
              <th className='width240px'>Activity</th>
              <th className='width175px'>Rate type</th>
              <th className='width150px'>No.of units</th>
              <th className='width150px'>Unit</th>
              <th className='width175px'>Rate per unit</th>
              <th className='width150px'>Cost</th>
              <th className='width225px'>Internal order</th>
              <th className='width225px'>Cost centre</th>
              <th className='width225px'>Fund</th>
              <th className='width225px'>Account</th>
              <th className='width150px'>Invoice no.</th>
            </tr>
          </thead>

          <tbody style={{ position: 'sticky', top: 0 }}>
            {flyingHours ? (
              flyingHours
                // .filter((x) => !x.deleted)
                .map((x: IFlyingHours, idx: number) =>
                  !x.deleted ? (
                    <tr key={idx}>
                      <td className='goa-checkbox-table-td'>
                        <GoACheckbox
                          name={'chkRow' + idx}
                          text=''
                          value={x.flightReportCostDetailsId}
                          checked={x.isChecked ?? false}
                          onChange={(name, checked, value) => {
                            checkRow(idx, checked);

                            if (!checked) setAllSelected(false);
                          }}
                        ></GoACheckbox>
                      </td>
                      <td>
                        <Select
                          name={'selFireNmbers' + idx}
                          options={x.fireNumberList}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={x.fireNumberList ? x.fireNumberList.find((t: { value: any }) => t.value === x?.fireNumberId) : null}
                          onChange={async (value: any) => {
                            setFireNumber(idx, value);
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) onFireNumberChange(idx, value);
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>
                        <Select
                          name={'selCrewTypeOnBoard' + idx}
                          options={x.crewTypeList}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={x.crewTypeList ? x.crewTypeList.find((t: { value: any }) => t.value === x?.crewTypeId) : null}
                          onChange={async (value: any) => {
                            setCrewType(idx, value);
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) onCrewTypeChange(idx, value);
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>
                        <Select
                          name={'selCostingActivity' + idx}
                          options={x.costingActivityList}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={
                            x.costingActivityId && x.costingActivityId !== EmptyGuid
                              ? x.costingActivityList?.find((t) => t.value === x.costingActivityId)
                              : null
                          }
                          onChange={async (value: any) => {
                            if (value.value) {
                              setCostingActivity(idx, value);
                              onValidate('selCostingActivity' + idx, 'onChange', value.label);
                            }
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) {
                              onCostingActivityChange(idx, value);
                            }
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>
                        <Select
                          name={'selRateType' + idx}
                          options={rateTypeOptions}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={rateTypeOptions.find((t: { value: any }) => t.value === x?.rateTypeId)}
                          onChange={async (value: IArrayOption | null) => {
                            if (!value) return;
                            if (value.value) {
                              await setRateType(idx, value);
                            }
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) onRateTypeChange(idx, value);
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>
                        <GoAFormItem error={x.noOfUnitsError}>
                          <GoAInput
                            name={'txtNoOfUnits' + idx}
                            value={x?.noOfUnits.toString()}
                            width='100%'
                            maxLength={10}
                            type='number'
                            error={x.isNoOfUnitsError}
                            onChange={(name, value) => {
                              if (value) {
                                setNoOfUnits(idx, value);
                              }
                            }}
                          />
                        </GoAFormItem>
                      </td>
                      <td>
                        <Select
                          name={'selRateUnit' + idx}
                          options={rateUnitOptions}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          isDisabled={contractRates && contractRates.find((value) => value.crtyId === x?.rateTypeEntity?.oracleId) ? true : false}
                          value={getDefaultRateUnit(x)}
                          onChange={async (value: any) => {
                            setRateUnit(idx, value);
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) onRateUnitChange(idx, value);
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>
                        {x.ratePerUnit &&
                        x.ratePerUnit > 0 &&
                        contractRates &&
                        contractRates.find((value) => value.crtyId === x?.rateTypeEntity?.oracleId) &&
                        !AuthenticationService.hasResourceRole('FlightReports', 'p_Aviat_FlightRptAppRet_E', auth.user?.access_token as string) ? (
                          '$' + x?.ratePerUnit
                        ) : (
                          <GoAFormItem error={x.ratePerUnitError}>
                            <GoAInput
                              name={'txtRatePerUnit' + idx}
                              value={x?.ratePerUnit ? x?.ratePerUnit.toString() : ''}
                              width='100%'
                              maxLength={10}
                              type='number'
                              leadingContent='$'
                              error={x.isRatePerUnitError}
                              onChange={(name, value) => {
                                if (value) {
                                  setRatePerUnit(idx, value);
                                }
                              }}
                            />
                          </GoAFormItem>
                        )}
                      </td>
                      <td>{'$' + x?.cost.toFixed(2)}</td>
                      <td>
                        <Select
                          name={'selInternalOrders' + idx}
                          options={x.internalOrderList}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={x.internalOrderList?.find((t) => t.value === x?.internalOrder) ?? null}
                          onChange={async (value: any) => {
                            setInternalOrder(idx, value);
                          }}
                          onInputChange={(value: any, actionMeta: InputActionMeta) => {
                            if (actionMeta.action === ActionEvents.inputChange) {
                              onInternalOrdersChange(idx, value);
                            }
                          }}
                          isSearchable={true}
                          isDisabled={!!x.fireNumber && !x.internalOrdersEnabled}
                        />
                      </td>
                      <td>
                        <Select
                          name={'selCostCenter' + idx}
                          options={x.costCenterList}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={x.costCenterList?.find((t: any) => t.value === x?.costCenter) ?? null}
                          onChange={async (value: any) => {
                            setCostCenter(idx, value);
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) onCostCenterChange(idx, value);
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>
                        <Select
                          name={'selFund' + idx}
                          options={x.fundList}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={x.fundList?.find((t: any) => t.value === x?.fund) ?? null}
                          onChange={async (value: any) => {
                            if (value.value) {
                              setFund(idx, value);
                            }
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) onFundChange(idx, value);
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>
                        <Select
                          name={'selAccount' + idx}
                          options={x.accountList}
                          placeholder={selectPlaceHolder}
                          className='width100'
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          menuPortalTarget={document.body}
                          value={x.accountList?.find((t: any) => t.value === x?.account) ?? null}
                          onChange={async (value: any) => {
                            if (value.value) {
                              setAccount(idx, value);
                            }
                          }}
                          onInputChange={(value: any, actionMeta: any) => {
                            if (actionMeta.action === ActionEvents.inputChange) onAccountChange(idx, value);
                          }}
                          isSearchable={true}
                        />
                      </td>
                      <td>{x?.invoiceNumber}</td>
                    </tr>
                  ) : null,
                )
            ) : (
              <></>
            )}
          </tbody>
        </GoATable>
      </div>
      <div className='flying-hours'>
        <GoAButton type='secondary' trailingIcon='add' disabled={noFlyDay} onClick={addNew}>
          Add new
        </GoAButton>
      </div>
    </>
  );
};

export default FlyingHoursEditTable;
